import { Suspense, useContext, useEffect, useState } from 'react'
import { MarkupContext } from '../../providers/Markup'
import MetaBadge from '../helpers/MetaBadge'
import { LockClosedIcon, XMarkIcon } from '@heroicons/react/24/solid'
import ButtonEl from '../helpers/ctas/Button'
import { addBgImageClass, getSectionTheme, lightOrDark, normalizeHex } from '../../helpers/style'
import createGradientElement from '../../helpers/markup'
import Loader from '../helpers/Loader'
import CuratedCardTitle from '../helpers/CuratedCardTitle'
import Modal from 'react-modal'
import { dataLayerPush } from '../../helpers/thirdparty'
import { resolveCTALabel } from '../../helpers/ctaLabel'
Modal.setAppElement('#root')

const stripHtml = (html) => (html ? html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim() : '')

const customModalStyle = {
  content: {
    top: '50%',
    left: '50%',
    width: '75vw',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    translate: '-50% -50%',
    aspectRatio: '16/10',
    maxHeight: '95vh',
  },
}

async function getYTEmbed(url) {
  try {
    const response = await fetch(`https://www.youtube.com/oembed?url=${url}&format=json`)
    const responseJson = await response.json()
    return responseJson
  } catch (error) {
    console.error(error)
  }
}

const BackgroundImageCard = (props) => {
  const { createMarkup } = useContext(MarkupContext)

  const [modalIsOpen, setIsOpen] = useState(false)
  const [videoEmbed, setVideoEmbed] = useState(null)
  const [gradientColor, setGradientColor] = useState('#fff')

  const { cardItem, i, isMember = null, onLockedClick } = props

  const badgeData = cardItem?.metaBadgeData || {}
  const firstCategoryTag = badgeData?.categoryTags?.[0]
  const isVisuallyLocked = !!badgeData.isLocked && isMember === false

  const handleCardClick = (e) => {
    if (isVisuallyLocked) {
      e.preventDefault()
      dataLayerPush({ event: 'login_intent', click_text: stripHtml(cardItem.title), member_only: true }, e.currentTarget)
      onLockedClick?.()
      return
    }
    dataLayerPush({
      event: 'article_click',
      click_text: stripHtml(cardItem.title),
      category: badgeData.categoryTags?.length ? badgeData.categoryTags.join(', ') : undefined,
      member_only: false,
    }, e.currentTarget)
  }

  useEffect(() => {
    const fgColor = props.imageGradientColour?.replace('#', '') || ''
    setGradientColor((prevState) => (fgColor ? `#${fgColor}` : prevState))
  }, [])

  function openModal() {
    setIsOpen(true)
  }

  async function afterOpenModal() {
    const embedCode = getYTEmbed(cardItem.videoUrl)
    embedCode.then((res) => {
      if (!res.error) {
        setVideoEmbed(res)
      }
    })
  }

  function closeModal() {
    setIsOpen(false)
  }

  const backgroundColor = normalizeHex(props.cardBackgroundColour)
  const lightOrDarkValue = lightOrDark(backgroundColor || normalizeHex(props.backgroundColor) || 'transparent')

  return (
    <div
      data-bg={getSectionTheme(backgroundColor)}
      style={{
        '--mobile-bg-url': cardItem.mobileImage ? `url(${cardItem.mobileImage.src})` : null,
        '--desktop-bg-url': cardItem.desktopImage ? `url(${cardItem.desktopImage.src})` : null,
        '--bg-color': backgroundColor || 'transparent',
        '--light-or-dark': lightOrDarkValue,
        '--text-color':
          lightOrDarkValue === 'dark' || cardItem.desktopImage || cardItem.mobileImage
            ? 'var(--color-light)'
            : 'var(--color-dark)',
      }}
      className={`w-full pt-4 px-8 pb-8 rounded-e-3xl rounded-bl-3xl bg-(--bg-color) text-(--text-color) ${lightOrDarkValue === 'light' ? 'border-1 border-(--color-border-tertiary)' : ''} h-full overflow-hidden ${addBgImageClass(cardItem.desktopImage?.src, cardItem.mobileImage?.src)}`}
    >
      {props.imageGradientColour &&
        createGradientElement({
          type: props.imageGradientType,
          fromColor: gradientColor,
        })}
      <div
        className={`relative min-h-96 h-full mt-auto flex flex-col ${cardItem.videoUrl ? 'justify-between' : 'justify-end'}`}
      >
        {cardItem.videoUrl ? (
          <ButtonEl className="h-20 w-20 mx-auto mt-20" onClick={openModal} theme="none">
            <PlayIcon role="none" className="text-current" />
            <span className="sr-only">Play video</span>
          </ButtonEl>
        ) : null}
        <div>
          {(firstCategoryTag || badgeData?.readTime || badgeData?.publicationDate) && (
            <div className="flex flex-row items-center w-full mb-4">
              <div className="flex-1">
                {(firstCategoryTag || badgeData?.readTime) && (
                  <MetaBadge
                    primaryText={firstCategoryTag}
                    secondaryText={badgeData?.readTime}
                    isLocked={!!badgeData?.isLocked}
                    icon={badgeData?.isLocked ? <LockClosedIcon className="w-3.5 h-3.5" aria-hidden="true" /> : null}
                    tagType={!!badgeData?.isLocked ? 'locked_news_articles' : 'unlocked_news_articles'}
                  />
                )}
              </div>
              {badgeData?.publicationDate && (
                <span className="text-xs font-normal shrink-0">{badgeData?.publicationDate}</span>
              )}
            </div>
          )}
          {cardItem.title && (
            <CuratedCardTitle
              title={cardItem.title}
              useSmallTitle={cardItem.useSmallTitle}
              titleAlignment={cardItem.titleAlignment}
              className="block"
            />
          )}
          {cardItem.description && (
            <div
              className="mt-4 md:mt-8"
              data-rte="true"
              dangerouslySetInnerHTML={createMarkup(cardItem.description)}
            />
          )}
          {cardItem.ctaLinkItem && cardItem.ctaLinkItem.length !== 0 && (
            <div
              className={`flex flex-col items-${props.textAlignment.flex === 'around' ? 'start' : props.textAlignment.flex} gap-6 mt-10`}
            >
              {cardItem.ctaLinkItem.map((ctaLinkItem, idx) => (
                <ButtonEl
                  key={ctaLinkItem.link?.url || idx}
                  link={ctaLinkItem.link}
                  label={resolveCTALabel(cardItem, ctaLinkItem, isMember)}
                  theme={ctaLinkItem.linkType.toLowerCase()}
                  icon={ctaLinkItem.linkIconClass}
                  position={ctaLinkItem.linkPosition}
                  onClick={handleCardClick}
                />
              ))}
            </div>
          )}
        </div>
      </div>
      {cardItem.videoUrl ? (
        <Modal
          isOpen={modalIsOpen}
          onAfterOpen={afterOpenModal}
          onRequestClose={closeModal}
          style={customModalStyle}
          contentLabel="Example Modal"
          aria={{
            labelledby: `modal-heading-${i}`,
          }}
        >
          <ButtonEl className="right-5 absolute" onClick={closeModal} theme="none">
            <XMarkIcon role="none" className="w-6 h-6 shrink text-current" />
            <span className="sr-only">Close this alert</span>
          </ButtonEl>
          <div data-type="video-modal-content" className="m-0 relative w-full">
            {videoEmbed && (
              <div id={`modal-heading-${i}`} className="absolute -top-[1000px] opacity-0">
                {videoEmbed.title}
              </div>
            )}
            <Suspense fallback={<Loader />}>
              {videoEmbed && (
                <div
                  data-type="video-iframe-wrapper"
                  className="w-full h-full"
                  data-rte="true"
                  dangerouslySetInnerHTML={createMarkup(videoEmbed.html)}
                />
              )}
            </Suspense>
          </div>
        </Modal>
      ) : null}
    </div>
  )
}

export default BackgroundImageCard
